<template>
    <v-card :elevation="elevation" class="flex-grow-1 d-flex flex-column" :class="{ 'graph-mobile-compact': mobileCompact }">
        <v-card-title
            v-if="title || $slots.title"
            class="pb-0 d-flex flex-wrap flex-sm-nowrap align-center"
        >
            <div v-if="title" class="d-flex align-center flex-grow-1 ellipsis">
                <span class="mr-4">{{ title }}</span>
            </div>
            <slot name="title" />
            <v-btn v-if="mobileCompact && $vuetify.breakpoint.xsOnly && showPeriodSelect" icon :aria-label="$translate('settings')" @click="settingsOpen = true"><v-icon small>mdi-tune-variant</v-icon></v-btn>
            <Select
                v-if="showPeriodSelect && !(mobileCompact && $vuetify.breakpoint.xsOnly)"
                class="flex-grow-0 mb-2 mb-sm-0 mt-2 mt-sm-0 w-100 w-sm-auto ml-0 ml-sm-4"
                :value="preset"
                :outlined="true"
                :dense="true"
                :clearable="false"
                :options="periods"
                @input="$emit('preset-changed', $event)"
            />
        </v-card-title>
        <v-dialog v-model="settingsOpen" max-width="480">
            <v-card>
                <v-card-title>{{ title }}</v-card-title>
                <v-card-text class="pt-4"><Select :value="preset" :outlined="true" :dense="true" :clearable="false" :options="periods" @input="$emit('preset-changed', $event)" /></v-card-text>
                <v-card-actions class="pa-4"><v-spacer /><v-btn text @click="settingsOpen = false">{{ $translate('close') }}</v-btn></v-card-actions>
            </v-card>
        </v-dialog>
        <v-card-text class="flex-grow-1 d-flex flex-column">
            <component
                v-if="!isEmpty && !loading"
                :is="chartComponent"
                :x-axis-type="xAxisType"
                :y-axis-type="yAxisType"
                :unit="unit"
                :height="height"
                width="100%"
                :key="JSON.stringify(chartData)"
                :chart-data="chartData || {}"
                :aspect-ratio="aspectRatio"
                :chart-id="chartId"
            />
            <Loading :absolute="false" :size="height" v-if="loading" />
            <slot name="empty" v-else-if="isEmpty">
                <div
                    class="flex-grow-1 my-12 d-flex flex-column justify-center align-center"
                >
                    <MasterSvg :use="emptyIcon" />
                    <div class="mt-2">{{ $translate("noData") }}</div>
                </div>
            </slot>
        </v-card-text>
    </v-card>
</template>

<script>
import utils from "@/utils.js";
import Line from "@/components/charts/line";
import Doughnut from "@/components/charts/doughnut";
import Bar from "@/components/charts/bar";
import GlobalMixins from "@/mixins/global";

export default {
    mixins: [GlobalMixins],
    props: {
        mobileCompact: Boolean,
        emptyIcon: { type: String, default: "nodata" },
        preset: {
            type: String,
            required: true,
        },
        unit: {
            type: String,
            required: false,
            default: "",
        },
        chartData: {
            type: Object,
            required: true,
        },
        chartId: {
            type: String,
            required: true,
        },
        type: {
            type: String,
            default: "line",
        },
        title: {
            type: String,
            required: false,
        },
        xAxisType: {
            type: String,
            required: false,
        },
        yAxisType: {
            type: String,
            required: false,
        },
        height: {
            type: Number,
            default: 200,
        },
        aspectRatio: {
            type: Number,
            default: 2.5,
        },
        elevation: {
            type: Number,
            default: 0,
        },
        loading: {
            type: Boolean,
            required: false,
            default: false,
        },
        periodOptions: {
            type: Array,
            required: false,
            default: null,
        },
        showPeriodSelect: {
            type: Boolean,
            default: true,
        },
    },
    computed: {
        chartComponent() {
            if (this.type == "doughnut") {
                return Doughnut;
            }
            if (this.type == "bar") {
                return Bar;
            }
            return Line;
        },
        periods() {
            if (this.periodOptions) {
                return this.periodOptions;
            }
            return [
                {
                    text: this.$translate("last12Months"),
                    value: "last12months",
                },
                {
                    text: this.$translate("thisYear"),
                    value: "thisyear",
                },
                {
                    text: this.$translate("lastYear"),
                    value: "lastyear",
                },
                {
                    text: this.$translate("allTime"),
                    value: "alltime",
                },
            ];
        },
        isEmpty() {
            if (!this.chartData || !this.chartData.datasets) {
                return true;
            }
            return !this.chartData.datasets.reduce((total, dataset) => {
                return total + (dataset.data ? dataset.data.length : 0);
            }, 0);
        },
    },
    methods: {
        async getPeriod(value) {
            if (value == "thisyear") {
                return {
                    start: this.$moment().startOf("year").format("YYYY-MM-DD"),
                };
            } else if (value == "alltime") {
                return {
                    start: null,
                    end: null,
                };
            } else if (value == "lastyear") {
                return {
                    start: this.$moment()
                        .startOf("year")
                        .subtract(1, "day")
                        .startOf("year")
                        .format("YYYY-MM-DD"),
                    end: this.$moment()
                        .startOf("year")
                        .subtract(1, "day")
                        .format("YYYY-MM-DD"),
                };
            } else if (value == "last12months") {
                return {
                    start: this.$moment().subtract(1, "year").format("YYYY-MM-DD"),
                };
            } else if (value == "last30days") {
                return {
                    start: this.$moment().subtract(30, "days").format("YYYY-MM-DD"),
                    scale: "day",
                };
            }
            return {};
        },
        async periodChanged(value) {
            if (!this.showPeriodSelect) {
                return;
            }
            const period = await this.getPeriod(value);
            this.$emit("period-changed", period);
        },
    },
    watch: {
        preset: {
            immediate: true,
            handler: function (value) {
                this.periodChanged(value);
            },
        },
    },
    data() {
        return {
            utils: utils,
            settingsOpen: false,
        };
    },
    i18n: {
        messages: {
            en: {
                last12Months: "Last 12 months",
                thisYear: "This year",
                lastYear: "Last year",
                allTime: "All time",
                settings: 'Chart settings', close: 'Close',
                noData: "No data to display",
            },
            fr: {
                last12Months: "Les 12 derniers mois",
                thisYear: "Cette année",
                lastYear: "L'année dernière",
                allTime: "Depuis toujours",
                settings: 'Réglages du graphique', close: 'Fermer',
                noData: "Aucune donnée à afficher",
            },
        },
    },
};
</script>

<style scoped>
@media (max-width: 599px) {
    .graph-mobile-compact > .v-card__title, .graph-mobile-compact > .v-card__text { padding-left: 0; padding-right: 0; }
}
</style>
